/**
 * dataSourceAccess - the people on a data source: read-side and write-side.
 *
 * WHY THIS EXISTS. The Access column on the data-sources LIST drew grey
 * silhouettes while the detail header drew real faces off the same records.
 * The list hand-rolled its own `{ name, initials, avatarURL }` mapping over
 * `ds.collaborators` and read the picture from ONE key, `s.avatarUrl` - so a
 * row that carries it as `profile_picture` or `avatar_url` lost it, and every
 * face fell through to initials-on-grey. The detail page never had the bug
 * because it hands `shared_with` straight to `CollaboratorStack`, which
 * normalises through `accessModel` and tries every avatar key there is.
 *
 * So there is no mapping here - only the choice of which key the payload put
 * the people under. The rows go to `CollaboratorStack` / `normaliseAll` raw.
 *
 * ITS OWN MODULE so the key-selection can be pinned by tests without importing
 * the page (and with it the store, the tour and the PubSub hooks).
 */

/**
 * `collaborators` if the payload filled it, else `shared_with`. Rows with no
 * identity at all (no name, no email, no initials) are dropped - they would
 * render as an anonymous grey disc, which is the thing being fixed.
 */
export const dataSourceAccessRows = (ds: any): any[] => {
  const rows: any[] = ds?.collaborators?.length ? ds.collaborators : ds?.shared_with || [];
  return (Array.isArray(rows) ? rows : []).filter(
    (s) => s && (s.name || s.Name || s.email || s.Email || s.initials || s.full_name || s.FullName)
  );
};

/** One person on the wire, in the shape the backend's normaliser reads. */
export interface DataSourceShare {
  id?: string | number;
  collaborator_uuid?: string;
  access: 'view' | 'edit' | 'admin';
}

/**
 * The create form's collaborator list → the `shared_with` payload.
 *
 * WHY AN OBJECT AND NOT A STRING. This used to be
 * `c.collaborator_uuid || String(c.user_id)`, which cost two things. The
 * access level, because a bare identifier has nowhere to carry one and the
 * backend defaults those to `view` - so everyone arrived read-only however
 * the selector was set, and the selector adds people as `edit`. And the
 * person, when they came from the org-people API with a `collaborator_uuid`
 * and no legacy `user_id`: `String(undefined)` is the literal `"undefined"`,
 * an identifier that resolves to nobody.
 *
 * `_normalize_collaborator_entries` on the backend takes the identifier from
 * the first of `collaborator_uuid`, `user_uuid`, `uuid`, `id`, `user_id` that
 * is present and the level from `access`. This is the same shape
 * `CreateTableForm` sends.
 */
export const dataSourceSharePayload = (collaborators: any[] | undefined | null): DataSourceShare[] =>
  (Array.isArray(collaborators) ? collaborators : [])
    .filter((c) => c && (c.collaborator_uuid || c.user_id != null))
    .map((c) => ({
      id: c.user_id,
      collaborator_uuid: c.collaborator_uuid,
      access: (c.access || 'view') as DataSourceShare['access'],
    }));

export default dataSourceAccessRows;
